import React from 'react';
import clsx from 'clsx';
import { Icons } from '@shared/ui';
import { Picture } from 'app/components/Picture';
import { getDynamicImageSource } from 'app/libs/url';
import { type BuyerLayoutProps } from 'app/shop-ui/types';
import { useBuyerProductStore, type BuyerProductAction } from 'app/store/product';
import STYLE from './ProductInfoGroup.module.scss';

const defaultSpecMeta: SpecMeta[] = [
    {
        group_name: '選取規格',
        layer: 1,
        specs: [
            {
                id: 'defaultSpecMetaId1',
                name: '規格 1',
                image: {
                    id: 'defaultSpecMetaImageId1',
                    src: '',
                    type: 'image',
                },
            },
            {
                id: 'defaultSpecMetaId2',
                name: '規格 2',
                image: {
                    id: 'defaultSpecMetaImageId2',
                    src: '',
                    type: 'image',
                },
            },
        ],
    },
    {
        group_name: '顏色',
        layer: 2,
        specs: [
            {
                id: 'defaultSpecMetaId3',
                name: 'red',
                image: {
                    id: 'defaultSpecMetaImageId3',
                    src: '',
                    type: 'image',
                },
            },
            {
                id: 'defaultSpecMetaId4',
                name: 'blue',
                image: {
                    id: 'defaultSpecMetaImageId4',
                    src: '',
                    type: 'image',
                },
            },
        ],
    },
    {
        group_name: '尺寸',
        layer: 3,
        specs: [
            {
                id: 'defaultSpecMetaId5',
                name: 'xs',
                image: {
                    id: 'defaultSpecMetaImageId5',
                    src: '',
                    type: 'image',
                },
            },
            {
                id: 'defaultSpecMetaId6',
                name: 's',
                image: {
                    id: 'defaultSpecMetaImageId6',
                    src: '',
                    type: 'image',
                },
            },
        ],
    },
];

interface ProductSpecItemPropType extends BuyerLayoutProps {
    name: string;
    options: Spec[];
    isStandardType: boolean;
    layerIndex: number;
    activeName: string | null;
    onSelect: BuyerProductAction['setSpecMetaLayerNameList'];
}

export const ProductSpecItem: React.FC<ProductSpecItemPropType> = (props) => {
    const { name, options, isStandardType, activeName, layerIndex, onSelect, isBuyer } =
        props;

    return (
        <div className={STYLE.productSpecBox}>
            <label htmlFor={name} className={STYLE.label}>
                {name}
            </label>
            {isStandardType && (
                <div className={STYLE.specPicker}>
                    <div className={STYLE.selectorArrow}>
                        <Icons.csr.CaretDown width="20px" height="20px" weight="bold" />
                    </div>
                    <select
                        name={name}
                        id={name}
                        className={STYLE.specPickerInner}
                        onChange={(e) => {
                            const [_name, id] = e.target.value.split('-');

                            onSelect(layerIndex, _name, id);
                        }}
                    >
                        {options.map((option) => (
                            <option
                                key={option.id}
                                value={`${option.name}-${option.id}`}
                                selected={option.name === activeName}
                            >
                                {option.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {!isStandardType && (
                <div className={STYLE.customOptionBox}>
                    {options.map((option, idx) => (
                        <button
                            key={option.id + idx}
                            className={clsx(
                                option.image.src ? STYLE.imageOption : STYLE.textOption,
                                (activeName === option.name || (!isBuyer && idx === 0)) &&
                                    STYLE.active
                            )}
                            onClick={() => {
                                onSelect(layerIndex, option.name, option.id);
                            }}
                        >
                            {option.image.src ? (
                                <Picture
                                    sources={[
                                        {
                                            ...getDynamicImageSource(option.image.src),
                                        },
                                    ]}
                                    alt={option.name}
                                />
                            ) : (
                                option.name
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

interface ProductSpecPickerProps extends BuyerLayoutProps {
    type: string;
    isEditingMode?: boolean;
}

export const ProductSpecPicker: React.FC<ProductSpecPickerProps> = (props) => {
    const { type, isEditingMode, isBuyer } = props;

    const isStandardType = type === 'standard';

    const productStore = useBuyerProductStore();

    const specMeta = isBuyer ? productStore.product?.spec_metas || [] : defaultSpecMeta;

    return (
        <span className={clsx(STYLE.productSpecPicker, isEditingMode && STYLE.disabled)}>
            {specMeta.map((i, idx) => (
                <ProductSpecItem
                    key={i.group_name + idx}
                    name={i.group_name}
                    options={i.specs}
                    layerIndex={idx}
                    isStandardType={isStandardType}
                    activeName={productStore.specMetaLayerNameList[idx]}
                    onSelect={productStore.setSpecMetaLayerNameList}
                    isBuyer={isBuyer}
                />
            ))}
        </span>
    );
};
